import React, { useState } from "react";
import Link from "@/utils/link";
import Image from "next/legacy/image";

import { imageLoader } from "@/utils/image";

import { CheckCircleIcon } from "@heroicons/react/outline";

import GetStartedSearch from "../Logo/GetStartedSearch";
import GetStartedLearn from "../Logo/GetStartedLearn";
import GetStartedCreateAccount from "../Logo/GetStartedCreateAccount";
import GetStartedConnect from "../Logo/GetStartedConnect";
import PurpleDoorLogoRect from "../Logo/PurpleDoorLogoRect";
import { PurpleDoorBelieves } from "./HomeRegister";
import HomeOurValues from "./HomeOurValues";
import HomeSearch from "./HomeSearch";
import HomeHeader from "./HomeHeader";
import HomeFooter from "./HomeFooter";

const HowItWorks = () => {
    return (
        <div className="">
            <HomeHeader fixed={true} />

            <div className="pt-12">
                <div className="hidden sm:block relative w-full h-[420px] bg-gradient-to-r from-black via-black to-transparent">
                    <Image
                        loader={imageLoader}
                        src="https://cdn.purpledoorfinders.com/purpledoorfinders/how-it-works-new"
                        alt="purple-door-finders-articles"
                        layout="fill"
                        objectFit="cover"
                        objectPosition="0px -100px"
                        priority
                        unoptimized={false}
                        className="opacity-60"
                    />

                    <div className="relative container mx-auto px-16 pt-32">
                        <h1 className="text-5xl text-white font-headline font-bold">
                            How It Works
                        </h1>

                        <p className="mt-4 text-white text-xl max-w-lg">
                            Shop and compare communities with real-time vacancies. When you
                            find one you like, skip the phone calls and save your time. No
                            hold music. No repeating your story. No sales pressure.
                        </p>
                        <p className="mt-4 text-white text-xl max-w-lg">
                            Simply use our <strong>“Request a Tour”</strong> or{" "}
                            <strong>“Ask a Question”</strong> features. Purple Door puts you
                            in control of when and how you connect with communities.
                        </p>
                    </div>
                </div>

                <div className="block sm:hidden relative w-full h-[420px] bg-gradient-to-r from-black via-black to-transparent">
                    <Image
                        loader={imageLoader}
                        src="https://cdn.purpledoorfinders.com/purpledoorfinders/how-it-works-new"
                        alt="purple-door-finders-articles"
                        layout="fill"
                        objectFit="cover"
                        objectPosition="0px 0px"
                        priority
                        unoptimized={false}
                        className="opacity-50"
                    />

                    <div className="relative container mx-auto px-4 pt-16">
                        <h1 className="text-3xl text-white font-headline font-bold">
                            How It Works
                        </h1>

                        <p className="mt-4 text-white text-xl max-w-lg">
                            Shop and compare communities with real-time vacancies. When you
                            find one you like, skip the phone calls and save your time. No
                            hold music. No repeating your story. No sales pressure.
                        </p>
                        <p className="mt-4 text-white text-xl max-w-lg">
                            Simply use our <strong>“Request a Tour”</strong> or{" "}
                            <strong>“Ask a Question”</strong> features. Purple Door puts you
                            in control of when and how you connect with communities.
                        </p>
                    </div>
                </div>

                <div className="py-6 sm:py-12 bg-gray-200">
                    <div className="flex justify-center">
                        <h2 className="text-xl sm:text-3xl text-primary-500 font-bold font-headline">
                            Start Your Search Now
                        </h2>
                    </div>

                    <div className="mt-6 sm:mt-8 flex justify-center">
                        <HomeSearch
                            inputClassName="w-[280px] sm:w-[500px]"
                            enableLocation={true}
                        />
                    </div>
                </div>

                <div className="container mx-auto px-4 py-4 sm:px-16 sm:py-12">
                    <h2 className="text-3xl text-primary-500 font-bold font-headline">
                        Easily manage your search based on your preferences or needs.
                    </h2>

                    <div className="max-w-2xl">
                        <p className="mt-4 text-base">
                            Search your way. Filter by your preferences, compare
                            communities, and connect instantly — all in one place.
                        </p>
                    </div>

                    <div className="mt-8 pb-8 grid grid-cols-1 xl:grid-cols-2 gap-x-16 gap-y-8">
                        <div className="flex items-center space-x-8">
                            <div>
                                <GetStartedSearch width={64} height={64} />
                            </div>

                            <div className="">
                                <h3 className="text-lg text-primary-500 font-bold font-headline">
                                    Search.
                                </h3>

                                <p className="">
                                    Start by entering the name of the city where you’d like
                                    to explore communities. You’ll see detailed information
                                    for each location, along with options to{" "}
                                    <strong>“Request a Tour”</strong> or{" "}
                                    <strong>“Ask a Question”</strong>.
                                </p>
                            </div>
                        </div>

                        <div className="flex items-center space-x-8">
                            <div>
                                <GetStartedLearn width={64} height={64} />
                            </div>

                            <div className="">
                                <h3 className="text-lg text-primary-500 font-bold font-headline">
                                    Learn.
                                </h3>

                                <p className="">
                                    Starting the search for senior living can feel
                                    overwhelming. That’s why we’ve created helpful tools to
                                    guide you every step of the way. Visit our Resources
                                    page to explore the <strong>Assessment Tool</strong>,{" "}
                                    <strong>Financial Calculator</strong>, and{" "}
                                    <strong>Living Preferences Guide</strong>. These tools
                                    help you understand your budget, clarify your needs, and
                                    identify the type of senior living that interests you.
                                </p>
                            </div>
                        </div>

                        <div className="flex items-center space-x-8">
                            <div>
                                <GetStartedCreateAccount width={64} height={64} />
                            </div>

                            <div className="">
                                <h3 className="text-lg text-primary-500 font-bold font-headline">
                                    Create Account.
                                </h3>

                                <p className="">
                                    Sign up to keep track of the communities you love and
                                    share them with family members. Your contact information
                                    will be saved, making it simple to request tours or ask
                                    questions without starting from scratch each time.
                                </p>
                            </div>
                        </div>

                        <div className="flex items-center space-x-8">
                            <div>
                                <GetStartedConnect width={64} height={64} />
                            </div>

                            <div className="">
                                <h3 className="text-lg text-primary-500 font-bold font-headline">
                                    Connect.
                                </h3>

                                <p className="">
                                    When you find a community you’re interested in, reaching
                                    out is simple. Just click{" "}
                                    <strong>“Request a Tour”</strong> or{" "}
                                    <strong>“Ask a Question”</strong> — no hold times or
                                    repeated phone calls. The community will respond
                                    directly to you.
                                </p>
                            </div>
                        </div>

                        {/*items.map((item) => (
                            <Item key={item.title} {...item} />
                        ))*/}
                    </div>
                </div>

                <div className="container mx-auto px-4 sm:px-16 sm:py-12">
                    <p className="mt-4 text-xl text-center text-primary-500 font-bold font-headline">
                        Purple Door Finders believes that people come first, and that people
                        want to make informed, confident decision about senior living.
                    </p>

                    <h2 className="mt-8 text-center text-3xl text-primary-500 font-bold font-headline">
                        That’s why we do things differently.
                    </h2>

                    {/*<div className="py-8 flex justify-center">
                        <div className="max-w-xl">
                            <ul className="text-xl space-y-4">
                                <li className="flex items-center">
                                    <CheckCircleIcon className="mr-4 w-16 h-16 shrink-0 text-primary-500" />
                                    The most comprehensive, accessible source for senior and
                                    assisted living listings.
                                </li>

                                <li className="flex items-center">
                                    <CheckCircleIcon className="mr-4 w-16 h-16 shrink-0 text-primary-500" />
                                    The ability to directly connect with communities.
                                </li>

                                <li className="flex items-center">
                                    <CheckCircleIcon className="mr-4 w-16 h-16 shrink-0 text-primary-500" />
                                    No sales people, specialists or advisors that recommend
                                    communities that are paying them.
                                </li>

                                <li className="flex items-center">
                                    <CheckCircleIcon className="mr-4 w-16 h-16 shrink-0 text-primary-500" />
                                    No commissions for referrals.
                                </li>

                                <li className="flex items-center">
                                    <CheckCircleIcon className="mr-4 w-16 h-16 shrink-0 text-primary-500" />
                                    Free for residents and communities do not pay
                                    commissions. (They pay nominal monthly subscription to
                                    share more information with you.)
                                </li>
                            </ul>
                        </div>
                    </div>*/}
                </div>

                {/*<PurpleDoorBelieves />*/}

                {/*<OurStory />*/}

                <HomeOurValues />
            </div>

            <HomeFooter />
        </div>
    );
};

const Item = ({ title, Icon, content }) => {
    return (
        <div className="flex items-center space-x-8">
            <div>
                <Icon width={64} height={64} />
            </div>

            <div className="">
                <h3 className="text-lg text-primary-500 font-bold font-headline">
                    {title}.
                </h3>

                <p className="">{content}</p>
            </div>
        </div>
    );
};

export const OurStory = () => {
    return (
        <div className="relative py-16">
            <h2 className="flex justify-center text-3xl text-primary-500 font-bold font-headline">
                Our Story
            </h2>

            <p className="mt-8 mx-auto max-w-2xl text-center text-lg text-gray-800">
                Our name has a very special meaning. How often do you see purple doors on
                houses, buildings, anywhere? Not often. And when you start looking for them,
                they are hard to find.
            </p>

            <p className="mt-8 mx-auto max-w-2xl text-center text-lg text-gray-800">
                This is the same for senior living. Again, there are a lot of options, but
                not exactly what you are searching for. It’s like trying to find a{" "}
                <span className="font-bold text-black">purple door</span>.
            </p>

            <p className="mt-8 mx-auto max-w-2xl text-center text-lg text-gray-800">
                That's why we’ve built a platform that makes it easier for you to find{" "}
                <span className="font-bold text-black">your</span> purple door .
            </p>

            <div className="absolute top-8  opacity-20" style={{ left: "15%" }}>
                <PurpleDoorLogoRect width={200} height={400} scale={2} />
            </div>
        </div>
    );
};

export default HowItWorks;
